"use client";

import type { HomeCopy, LocaleKey } from "./copy";
import type { AgenciCopy } from "./agenci-copy";
import ClientBits from "./ClientBits";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";

interface Props {
  locale: LocaleKey;
  homeCopy: HomeCopy;
  copy: AgenciCopy;
}

export default function AgenciPage({ locale, homeCopy, copy }: Props) {
  return (
    <div
      className="hestia-page agenci-page"
      style={{ fontFamily: "var(--font-inter), Inter, sans-serif" }}
    >
      <div className="main-wrapper">
        <div className="main-overlay" />
        <SiteHeader copy={homeCopy} locale={locale} />

        {/* Block 1 — HERO */}
        <section className="hero-section container">
          <div className="hero-grid">
            <div className="hero-info">
              <h1 className="hero-title">
                {copy.hero.titleLine1}
                <br />
                <span className="accent">{copy.hero.titleLine2}</span>
              </h1>
              <p className="hero-text">{copy.hero.text}</p>
              <div className="btn-container">
                <button type="button" className="wa-btn shine-effect open-callback" data-cta-source="agenci-hero">
                  <div className="wa-btn-content">
                    <span className="wa-title">{copy.hero.primaryCta}</span>
                    <span className="wa-subtitle">{copy.hero.primaryCtaSub}</span>
                  </div>
                  <div className="wa-circle" style={{ background: "#C9A36A" }}>
                    <span style={{ fontSize: 22, fontWeight: 800, color: "#0F2542" }}>→</span>
                  </div>
                </button>
              </div>
              <p style={{ marginTop: 14, fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
                <a href={copy.hero.secondaryCtaHref} style={{ color: "#C9A36A", fontWeight: 700, textDecoration: "underline" }}>
                  {copy.hero.secondaryCta} →
                </a>
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Block 2 — DIFFERENCE (наш агент vs агент девелопера) */}
      <section id="roznica" className="kredyt-section kredyt-section--cream">
        <div className="container">
          <div className="kredyt-section-header">
            <h2 className="kredyt-h2">{copy.difference.h2}</h2>
            <p className="kredyt-subhead">{copy.difference.subhead}</p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 20,
              maxWidth: 920,
              margin: "0 auto",
            }}
          >
            {/* Наш агент */}
            <div
              style={{
                background: "#fff",
                border: "1px solid rgba(201,163,106,0.45)",
                borderRadius: 16,
                padding: "26px 24px",
                boxShadow: "0 8px 26px rgba(15,37,66,0.06)",
              }}
            >
              <h3 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 800, color: "#0F2542" }}>
                {copy.difference.headers.ours}
              </h3>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 12 }}>
                {copy.difference.rows.map((r, i) => (
                  <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 15, lineHeight: 1.55, color: "#1f2d3d" }}>
                    <span aria-hidden="true" style={{ color: "#1f8a4c", fontWeight: 800, flexShrink: 0 }}>✓</span>
                    <span>{r.ours}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Агент девелопера */}
            <div
              style={{
                background: "rgba(15,37,66,0.03)",
                border: "1px solid rgba(15,37,66,0.10)",
                borderRadius: 16,
                padding: "26px 24px",
              }}
            >
              <h3 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 800, color: "#5a6675" }}>
                {copy.difference.headers.theirs}
              </h3>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 12 }}>
                {copy.difference.rows.map((r, i) => (
                  <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 15, lineHeight: 1.55, color: "#5a6675" }}>
                    <span aria-hidden="true" style={{ color: "#a0a8b3", fontWeight: 800, flexShrink: 0 }}>·</span>
                    <span>{r.theirs}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p style={{ maxWidth: 720, margin: "24px auto 0", textAlign: "center", fontSize: 14, lineHeight: 1.6, color: "#5a6675" }}>
            {copy.difference.note}
          </p>
        </div>
      </section>

      {/* Block 3 — HOW WE WORK (макс 5 клиентов + команда) */}
      <section id="jak-pracujemy" className="kredyt-section kredyt-section--white">
        <div className="container">
          <div className="kredyt-section-header">
            <h2 className="kredyt-h2">{copy.howWeWork.h2}</h2>
            <p className="kredyt-subhead">{copy.howWeWork.subhead}</p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 20,
              maxWidth: 980,
              margin: "0 auto",
            }}
          >
            {copy.howWeWork.cards.map((card, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  border: "1px solid rgba(15,37,66,0.08)",
                  borderRadius: 16,
                  padding: "26px 24px",
                  boxShadow: "0 8px 26px rgba(15,37,66,0.05)",
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    width: 40,
                    height: 4,
                    borderRadius: 999,
                    background: "#C9A36A",
                    marginBottom: 16,
                  }}
                />
                <h3 style={{ margin: "0 0 10px", fontSize: 17, fontWeight: 800, color: "#0F2542" }}>
                  {card.title}
                </h3>
                <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: "#3a4757" }}>
                  {card.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Block 4 — PRINCIPLES */}
      <section id="zasady" className="kredyt-section kredyt-section--cream">
        <div className="container">
          <div className="kredyt-section-header">
            <h2 className="kredyt-h2">{copy.principles.h2}</h2>
            <p className="kredyt-subhead">{copy.principles.subhead}</p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 20,
              maxWidth: 980,
              margin: "0 auto",
            }}
          >
            {copy.principles.items.map((item, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  border: "1px solid rgba(201,163,106,0.30)",
                  borderRadius: 16,
                  padding: "26px 24px",
                  boxShadow: "0 8px 26px rgba(15,37,66,0.05)",
                }}
              >
                <h3 style={{ margin: "0 0 10px", fontSize: 18, fontWeight: 800, color: "#0F2542" }}>
                  {item.title}
                </h3>
                <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: "#3a4757" }}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Block 5 — JOIN TEAM (ссылка на /kariera) */}
      <section id="kariera" className="kredyt-section kredyt-section--white">
        <div className="container">
          <div
            style={{
              maxWidth: 760,
              margin: "0 auto",
              textAlign: "center",
              background: "rgba(15,37,66,0.03)",
              border: "1px solid rgba(15,37,66,0.08)",
              borderRadius: 18,
              padding: "36px 28px",
            }}
          >
            <h2 className="kredyt-h2" style={{ marginBottom: 12 }}>{copy.joinTeam.h2}</h2>
            <p style={{ margin: "0 auto 22px", maxWidth: 560, fontSize: 15, lineHeight: 1.6, color: "#3a4757" }}>
              {copy.joinTeam.body}
            </p>
            <a
              href={copy.joinTeam.ctaHref}
              style={{
                display: "inline-block",
                background: "#0F2542",
                color: "#fff",
                fontWeight: 700,
                fontSize: 15,
                padding: "14px 28px",
                borderRadius: 12,
                textDecoration: "none",
              }}
            >
              {copy.joinTeam.ctaLabel} →
            </a>
          </div>
        </div>
      </section>

      {/* Block 6 — FINAL CTA */}
      <section id="kontakt" className="kredyt-section kredyt-section--navy">
        <div className="container">
          <div className="kredyt-section-header">
            <h2 className="kredyt-h2" style={{ color: "#fff" }}>{copy.finalCta.h2}</h2>
            <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 15, lineHeight: 1.6, maxWidth: 620, margin: "0 auto" }}>
              {copy.finalCta.body}
            </p>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
            <button
              type="button"
              className="open-callback"
              data-cta-source="agenci-final"
              style={{
                background: "#C9A36A",
                color: "#0F2542",
                fontWeight: 800,
                fontSize: 16,
                padding: "16px 32px",
                borderRadius: 12,
                border: 0,
                cursor: "pointer",
              }}
            >
              {copy.finalCta.primaryCta}
            </button>
          </div>
          <p style={{ marginTop: 20, textAlign: "center", fontSize: 13, lineHeight: 1.6, color: "rgba(255,255,255,0.7)" }}>
            {copy.finalCta.trustLine}
          </p>
        </div>
      </section>

      <SiteFooter copy={homeCopy} />
      <ClientBits copy={homeCopy} locale={locale} />
    </div>
  );
}
